<script lang="ts" setup>
import type { SectionsBanner } from '@autobid/strapi-integration/rest/components'
import StrapiImage from '@autobid/ui/components/common/strapi/Image.vue'
import { addOpacityToStrapiText } from '@autobid/ui/utils/addOpacityToStrapiText'
import { bannerCva } from '@autobid/ui/utils/cva/bannerCva'

interface Props {
  data: SectionsBanner
}

const props = defineProps<Props>()

const isNavbarTransparent = inject<boolean>('isNavbarTransparent', false)
const images = computed(() => {
  const result = [{ isMobile: false, image: props.data.image }]

  if (props.data.imageMobile) {
    result.push({ isMobile: true, image: props.data.imageMobile })
  }

  return result
})

const theLowestImageHeight = computed(() => {
  const lowestImage = images.value.reduce(
    (highest, image) =>
      image.image?.url
      && image.image.height
      < (highest.image?.height ?? 0)
        ? image
        : highest,
    { isMobile: false, image: props.data.image },
  )
  return lowestImage?.image?.height
})

const renderDescription = computed(() => {
  if (props.data.url) {
    return false
  }

  return Boolean(props.data.description)
})
</script>

<template>
  <div class="full-width-component relative">
    <CommonLink
      v-if="data.url"
      :href="data.url"
      class="absolute inset-0 z-20"
    />

    <div
      class="section-banner relative z-[1] -mt-[1px] w-full overflow-hidden"
      :class="[
        bannerCva({ type: data.type }),
        {
          'mb-5': !props.data.marginBottomDisabled,
        },
      ]"
      :style="{
        '--min-height': `${theLowestImageHeight}px`,
      }"
    >
      <div
        class="item relative isolate z-[999] w-full xl:px-0"
        :class="{ 'min-height': renderDescription }"
      >
        <template v-for="image in images">
          <StrapiImage
            v-if="image.image?.url"
            :key="image.image.url"
            :data="image.image"
            :class="{
              'min-height absolute z-10 inset-0 h-full object-cover object-center': renderDescription || isNavbarTransparent,
              'md:hidden': image.isMobile,
              'hidden md:block': !image.isMobile && images.length > 1,
            }"
            class="w-full"
          />
        </template>

        <div
          v-if="renderDescription"
          class="container mx-auto h-full !px-0 !py-6"
        >
          <SectionsRichTextCkeditor
            class="relative z-[999] prose-sm md:prose-base lg:prose-lg"
            no-prose
            :class="[bannerCva({ color: data.descriptionColor?.name })]"
            :data="{
              width: 'w-full',
              content: data.textBackgroundOpacity
                ? addOpacityToStrapiText(
                  data.description,
                  data.textBackgroundOpacity,
                )
                : data.description,
            }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.has-transparent-nav {
  .item {
    --item-pad: 24px;

    padding-top: calc(var(--nav-height) + var(--item-pad));

    @media (min-width: 1024px) {
      padding-top: calc(
        var(--nav-height) + var(--desktop-nav-height) + var(--item-pad)
      );
    }
  }
}

@media (min-width: 768px) {
  .min-height {
    min-height: var(--min-height);
  }
}
.section-banner {
  span[style*='background-color'] {
    padding: 0.2rem 0.7rem;
    display: inline-block;
  }
}
</style>
